import React, { useRef, useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";

/* ─────────────────────────────────────────────────────────────────
   MatrixHeading — animates each letter through a 1/0 matrix flicker
   before settling on the real character. Triggers when `active` flips
   to true. Fires onComplete after the last letter settles so the
   parent can continue with the shimmer.

   Extracted from About.js so the same reveal can be used in Domains,
   Events, and any other section that wants the binary reveal effect.
───────────────────────────────────────────────────────────────── */
const MatrixHeading = ({
  text,
  active,
  onComplete,
  initialDelay = 200,
  letterAnimationDuration = 500,
  letterInterval = 100,
}) => {
  const buildInitialLetters = useCallback(
    () =>
      text.split("").map((char) => ({
        char,
        isMatrix: false,
        isSpace: char === " ",
      })),
    [text],
  );

  const [letters, setLetters] = useState(buildInitialLetters);

  /* keep latest onComplete in a ref so changing its identity doesn't
     restart the animation effect every render */
  const onCompleteRef = useRef(onComplete);
  useEffect(() => {
    onCompleteRef.current = onComplete;
  }, [onComplete]);

  const getRandomChar = useCallback(
    () => (Math.random() > 0.5 ? "1" : "0"),
    [],
  );

  const animateLetter = useCallback(
    (index) => {
      if (index >= text.length) return;

      requestAnimationFrame(() => {
        setLetters((prev) => {
          const next = [...prev];
          if (!next[index].isSpace) {
            next[index] = {
              ...next[index],
              char: getRandomChar(),
              isMatrix: true,
            };
          }
          return next;
        });

        setTimeout(() => {
          setLetters((prev) => {
            const next = [...prev];
            next[index] = {
              ...next[index],
              char: text[index],
              isMatrix: false,
            };
            return next;
          });
        }, letterAnimationDuration);
      });
    },
    [getRandomChar, text, letterAnimationDuration],
  );

  /* Run (or re-run) the matrix reveal whenever `active` flips to true.
     When `active` flips back to false we reset the letters so the next
     activation replays from a clean state. */
  useEffect(() => {
    if (!active) {
      setLetters(buildInitialLetters());
      return undefined;
    }

    let i = 0;
    const timers = [];

    const tick = () => {
      if (i >= text.length) {
        timers.push(
          setTimeout(
            () => onCompleteRef.current && onCompleteRef.current(),
            letterAnimationDuration + 50,
          ),
        );
        return;
      }
      animateLetter(i);
      i += 1;
      timers.push(setTimeout(tick, letterInterval));
    };

    timers.push(setTimeout(tick, initialDelay));

    return () => timers.forEach(clearTimeout);
  }, [
    active,
    animateLetter,
    text,
    initialDelay,
    letterInterval,
    letterAnimationDuration,
    buildInitialLetters,
  ]);

  return (
    <>
      {letters.map((letter, i) => (
        <motion.span
          key={i}
          animate={
            letter.isMatrix
              ? {
                  color: "#15B2AF",
                  WebkitTextFillColor: "#15B2AF",
                  textShadow: "0 2px 14px rgba(21, 178, 175, 0.55)",
                }
              : {
                  color: "inherit",
                  WebkitTextFillColor: "inherit",
                  textShadow: "none",
                }
          }
          transition={{ duration: 0.12, ease: "easeInOut" }}
          style={{
            display: "inline-block",
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {letter.isSpace ? " " : letter.char}
        </motion.span>
      ))}
    </>
  );
};

export default MatrixHeading;
